import * as Messages from "../codegen/Messages";

export interface LengthRange {
  /**
   * The minimum allowed length
   */
  min?: number;
  /**
   * The maximum allowed length
   */
  max?: number;
}

export interface ReservedWordValidation {
  /**
   * List of words that the value is not allowed to contain
   */
  words: string[];
  /**
   * Whether to match the whole value against the reserved word(s)
   * @default false
   */
  wholeWordMatch?: boolean;
  /**
   * Whether the words should be searched for case sensitive
   * @default false
   */
  caseSensitiveMatch?: boolean;
  /**
   * Custom error message
   * @default "The value cannot contain the reserved word '{reserved}'."
   * @default "The value cannot contain any of the following reserved words: {reserved}."
   */
  errorMessage?: string;
}

export interface RegExpValidation {
  /**
   * The regex to test the value against
   */
  regExp: RegExp;
  /**
   * The custom error message to be returned if the string does not match the regex
   */
  errorMessage: string;
}

export interface ValidateStringOptions {
  /**
   * When true it will not display validation errors if the value is empty/undefined
   * When false it will display validation errors if the value is empty/undefined
   * @default false
   */
  isOptional?: boolean;
  /**
   * The allowed range that the length of the value can belong to
   */
  lengthRange?: LengthRange;
  /**
   * Configuration for validating against a list of reserved words
   */
  reservedWordValidation?: ReservedWordValidation;
  /**
   * Configuration for validating against regular expressions
   */
  regExpValidation?: RegExpValidation | RegExpValidation[];
}

const validateOptions = (options: ValidateStringOptions): void => {
  const {
    lengthRange,
    regExpValidation,
    reservedWordValidation,
  } = options;

  if (!regExpValidation && !lengthRange && !reservedWordValidation) {
    throw new Error("An option must be provided to validateString.");
  }

  if (lengthRange) {
    const { min, max } = lengthRange;
    if (!min && !max) throw new Error("Min and/or max must be provided.");
  }

  if (reservedWordValidation) {
    const { words } = reservedWordValidation;
    if (words.length === 0) throw new Error("Reserved words cannot be empty");
  }
};

const validateReservedWords = (value: string, reservedWordValidation: ReservedWordValidation): boolean => {
  const {
    words: reservedWords,
    caseSensitiveMatch,
    wholeWordMatch,
  } = reservedWordValidation;

  const searchCaseSensitive = caseSensitiveMatch ?? false;
  const matchWholeWord = wholeWordMatch ?? false;

  const getNormalizedString = (val: string): string => {
    if (!searchCaseSensitive) return val.toLocaleLowerCase();
    return val;
  };

  const normalizedValue = getNormalizedString(value);

  let hasReservedWord: boolean;

  if (matchWholeWord) {
    hasReservedWord = reservedWords.some(word => normalizedValue === getNormalizedString(word));
  } else {
    hasReservedWord = reservedWords.some(word => normalizedValue.includes(getNormalizedString(word)));
  }

  return !hasReservedWord; // Negate as successful validation is no words being matched
};

const validateLengthRange = (value: string, lengthRange: LengthRange): boolean => {
  const {
    min,
    max,
  } = lengthRange;

  if (max && value.length > max) return false;
  if (min && value.length < min) return false;
  return true;
};

const getLengthRangeError = (lengthRange: LengthRange): string => {
  // eslint-disable-next-line eqeqeq
  if (!lengthRange.min && lengthRange.max != undefined) {
    return Messages.validation.valueMaxLen(lengthRange.max.toString());
  }
  // eslint-disable-next-line eqeqeq
  if (!lengthRange.max && lengthRange.min != undefined) {
    return Messages.validation.valueMinLen(lengthRange.min.toString());
  }

  return Messages.validation.inputRange(
    lengthRange.min?.toString() || "",
    lengthRange.max?.toString() || "",
  );
};

const getReservedWordsValidationError = (reservedWordValidation: ReservedWordValidation): string => {
  if (reservedWordValidation.errorMessage) return reservedWordValidation.errorMessage;
  if (reservedWordValidation.words.length === 1) {
    return Messages.validation.validationReservedWord(reservedWordValidation.words[0]);
  }
  return Messages.validation.validationReservedWords(reservedWordValidation.words.join(", "));
};

const getAllErrors = (options: ValidateStringOptions): string[] => {
  const errors: string[] = [];
  const { lengthRange, regExpValidation, reservedWordValidation } = options;

  if (lengthRange) {
    errors.push(getLengthRangeError(lengthRange));
  }

  if (regExpValidation) {
    if (Array.isArray(regExpValidation)) {
      regExpValidation.forEach(({ errorMessage }) => errors.push(errorMessage));
    } else {
      errors.push(regExpValidation.errorMessage);
    }
  }

  if (reservedWordValidation) {
    errors.push(getReservedWordsValidationError(reservedWordValidation));
  }

  return errors;
};

export const validateString = (value: string | undefined, options: ValidateStringOptions): string[] | undefined => {
  validateOptions(options);

  const { regExpValidation, lengthRange, reservedWordValidation, isOptional = false } = options;

  if (!value) {
    if (!isOptional) return getAllErrors(options);
    return undefined;
  }

  const errors: string[] = [];

  if (regExpValidation) {
    if (Array.isArray(regExpValidation)) {
      regExpValidation.forEach(({ errorMessage, regExp }) => {
        if (!regExp.test(value)) errors.push(errorMessage);
      });
    } else if (!regExpValidation.regExp.test(value)) {
      errors.push(regExpValidation.errorMessage);
    }
  }

  if (reservedWordValidation && !validateReservedWords(value, reservedWordValidation)) {
    errors.push(getReservedWordsValidationError(reservedWordValidation));
  }

  if (lengthRange && !validateLengthRange(value, lengthRange)) {
    errors.push(getLengthRangeError(lengthRange));
  }

  return errors.length > 0 ? errors : undefined;
};
